import { Box, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { ContentType } from 'recharts/types/component/DefaultLegendContent'
import {
  NameType,
  ValueType
} from 'recharts/types/component/DefaultTooltipContent'
import { ContentType as TooltipContentType } from 'recharts/types/component/Tooltip'
import { IDataEntry, PeriodTypes } from '../types/types'
import {
  groupLast12Months,
  groupLast24Hours,
  groupLast30Days,
  groupLast7Days,
  timeZone
} from './utils/processTimeIntervals'
import dayjs from 'dayjs'

type NumericValue = string | number | null

/**
 * Interface for the GraphBar component props.
 */
interface IGraphBarProps {
  /** Array of data entries for the graph */
  data: IDataEntry[][]
  /** Type of data being displayed (e.g., '%', 'bpm', 'mg/dL') */
  dataType: string
  /** Loading state for the graph */
  loading: boolean
  /** Periods for which the data is displayed (e.g., 'day', 'week', 'month', 'year') */
  periods: PeriodTypes
  /** Type of bar graph to render ('simple' or 'stacked') */
  type: 'simple' | 'stacked'
  /** Reference type for the bar graph ('sum' or 'avg') */
  reference?: 'sum' | 'avg'
  /** Optional function to format the value displayed in the graph */
  formatValue?: (value: NumericValue) => NumericValue
  /** Optional function to format the y-axis values */
  formatY?: (value: NumericValue) => NumericValue
  /** Optional y-axis domain for the graph */
  yAxisDomain?: [number, number]
  /** Optional flag to use end of day for the x-axis */
  useEndOfDay?: boolean
  /** Optional reference date for the graph */
  referenceDate?: dayjs.Dayjs | null
}

/**
 * GraphBar component for displaying a bar chart.
 * It supports both simple and stacked bar types and can display data over different periods.
 * @param {IGraphBarProps} props - Props for the component.
 * @returns {JSX.Element} - The rendered bar chart component.
 * @example
 * <GraphBar
 *  data={dataEntries}
 *  periods="month"
 *  dataType="%"
 *  loading={false}
 *  type="simple"
 *  reference="sum"
 *  formatValue={(value) => value ? `${value}%` : 'N/A'}
 *  formatY={(value) => value ? `${value} units` : 'N/A'}
 *  yAxisDomain={[0, 100]}
 * />
 */
const GraphBar: React.FC<IGraphBarProps> = ({
  data,
  dataType,
  loading,
  periods,
  type,
  reference = 'sum',
  formatValue,
  formatY,
  yAxisDomain = [0, 100],
  useEndOfDay = false,
  referenceDate
}) => {
  const theme = useTheme()
  const colors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main
    ],
    [theme]
  )

  const CustomLegend: ContentType = ({ payload }) => {
    return (
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {payload?.map((entry: any, index: number) => {
          const currentLegend = data[index][0]

          return (
            currentLegend && (
              <li key={'item-' + index} style={{ color: entry.color }}>
                <span style={{ marginRight: 8, fontWeight: 'bold' }}>⬤</span>
                {entry.value.includes(reference) &&
                  `${currentLegend.name} ${reference === 'avg' ? '(Média)' : ''}`}
              </li>
            )
          )
        })}
      </ul>
    )
  }

  const CustomTooltip: TooltipContentType<ValueType, NameType> = ({
    active,
    payload,
    label
  }) => {
    if (active && payload?.length) {
      return (
        <Box
          sx={{
            background: theme.palette.background.default,
            p: 1,
            gap: 2,
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography fontWeight='bold'>{`${label}`}</Typography>
          {datasets.map((_, dataIndex) => (
            <React.Fragment key={'dataset-' + dataIndex}>
              {payload?.map((entry: any, index: number) => (
                <React.Fragment key={'tooltip-entry-' + index}>
                  {entry.name === `${dataIndex}_${reference}` && (
                    <Typography sx={{ color: entry.color }}>
                      <b>{data[entry.dataKey.split('_')[0]][0].name}:</b>{' '}
                      {formatValue ? formatValue(entry.value) : entry.value}{' '}
                      {dataType}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
              <div style={{ height: 5 }} />
            </React.Fragment>
          ))}
        </Box>
      )
    }

    return null
  }

  const refDate = useMemo(() => {
    if (referenceDate) {
      return referenceDate.tz(timeZone)
    }
    if (useEndOfDay) {
      return dayjs().tz(timeZone).endOf('day')
    }
    return dayjs().tz(timeZone)
  }, [timeZone, referenceDate, useEndOfDay])

  const groupedData = {
    day: (data: IDataEntry[]) => groupLast24Hours(data, refDate),
    week: (data: IDataEntry[]) => groupLast7Days(data, refDate),
    month: (data: IDataEntry[]) => groupLast30Days(data, refDate),
    year: (data: IDataEntry[]) => groupLast12Months(data, refDate)
  }

  const datasets = data.map(hv => groupedData[periods](hv))
  const dataset = datasets.reduce(
    (acc, row, rowIndex) => {
      row.forEach((entry, colIndex) => {
        if (!acc[colIndex]) acc[colIndex] = {}
        acc[colIndex]['label'] = entry.label
        acc[colIndex][`${rowIndex}_sum`] = entry.sum ?? null
        acc[colIndex][`${rowIndex}_avg`] = entry.avg ?? null
      })
      return acc
    },
    [] as Record<string, number[] | string | number | null>[]
  )

  const renderGraph = {
    simple: (index: number) => (
      <Bar
        key={`${index}_${reference}`}
        dataKey={`${index}_${reference}`}
        barSize={20}
        radius={16}
        fill={colors[index % colors.length]}
        background={{
          fill: theme.palette.action.disabledBackground,
          radius: 16
        }}
        min={0}
      />
    ),
    stacked: (index: number) => (
      <Bar
        key={`${index}_${reference}`}
        dataKey={`${index}_${reference}`}
        stackId={index === 0 ? 'a' : 'b'}
        barSize={20}
        // hide={index === 0} // DOCUMENTATION: Hidden first value in StackedBar (like SleepTotal) what should be the sum of others values
        fill={colors[index % colors.length]}
      />
    )
  }

  return (
    <ResponsiveContainer>
      <ComposedChart
        width={1000}
        height={400}
        data={dataset}
        margin={{
          top: 20,
          right: 20,
          bottom: 20,
          left: 20
        }}
        style={
          loading
            ? {
                opacity: 0.5,
                transition: 'opacity 0.5s ease-in-out'
              }
            : {}
        }
      >
        <CartesianGrid stroke={theme.palette.divider} />
        <XAxis dataKey='label' />
        <YAxis
          tick={{ textAnchor: 'end' }}
          tickFormatter={value => (formatY ? formatY(value) : value)}
          domain={yAxisDomain}
        />
        <Tooltip content={CustomTooltip} />
        {!!datasets.length && <Legend content={CustomLegend} />}
        {datasets.map((_, index) => renderGraph[type](index))}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default GraphBar
